/**
===================================================
Module: frontend.src.components.Sessions.tsx
Description: Documents management page component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Session, Project, CreateSessionRequest, isSessionActive, SortOption, FilterOption } from "../types";
import { apiService, POLLING_INTERVAL_MS } from "../services/api";
import TopBar from "./TopBar";
import CreateSessionModal from "./CreateSessionModal";
import SessionsList from "./SessionsList";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/Sessions.css";
import { useSessionCreationStore } from "../stores/sessionCreationStore";
import { useAuth } from "../contexts/AuthContext";
import SortFilterModal from "./SortFilterModal";

const Sessions: React.FC = () => {
  const { t } = useTranslation('sessions');
  const { t: tProjects } = useTranslation('projects');
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Add notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const navigate = useNavigate();

  const { clearState, setSessionData } = useSessionCreationStore();

  const [showMenuDropdown, setShowMenuDropdown] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(
    new Set(),
  );
  const [isDeleting, setIsDeleting] = useState(false);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Retention logic
  const { user, refreshUser } = useAuth();
  const [retentionDays, setRetentionDays] = useState<number>(30);

  const addNotification = (notification: Omit<Notification, "id">) => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { ...notification, id }]);
    return id;
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  // Check if there are any active sessions
  const hasActiveSessions = (sessionsList: Session[]) => {
    return sessionsList.some(session => isSessionActive(session.status));
  };

  // Start polling for active sessions
  const startPolling = () => {
    if (pollingIntervalRef.current || !projectId) return;

    setIsPolling(true);
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const sessionsData = await apiService.pollSessions(projectId);
        setSessions(sessionsData);

        if (!hasActiveSessions(sessionsData)) {
          stopPolling();
          // Quota might have changed now that processing finished
          refreshUser();
        }
      } catch (error) {
        addNotification({
          type: 'error',
          message: t('error.pollFailed')
        });
      }
    }, POLLING_INTERVAL_MS);
  };

  // Stop polling
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      setIsPolling(false);
    }
  };

  // Initial data load
  useEffect(() => {
    if (projectId) {
      loadData();
    }

    // Cleanup polling on unmount
    return () => {
      stopPolling();
    };
  }, [projectId]);

  useEffect(() => {
    if (sessions.length > 0) {
      if (hasActiveSessions(sessions) && !pollingIntervalRef.current) {
        startPolling();
      } else if (!hasActiveSessions(sessions) && pollingIntervalRef.current) {
        stopPolling();
      }
    }
  }, [sessions]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [projectData, sessionsData] = await Promise.all([
        apiService.getProject(projectId!),
        apiService.getSessions(projectId!)
      ]);
      setProject(projectData);
      setSessions(sessionsData);

      // Effect retention days are now returned in the user profile (backend handles global fallback)
      setRetentionDays(user?.document_retention_days ?? 30);

    } catch (error) {
      addNotification({
        type: "error",
        message: t('error.loadFailed'),
        duration: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateSession = (
    sessionData: CreateSessionRequest,
    pdfFile: File,
    thumbnailBlob: Blob,
  ) => {
    setSessionData(sessionData);
    setShowCreateForm(false);

    let uploadNotificationId: string | undefined;

    const uploadSession = async () => {
      try {
        setIsUploading(true);

        uploadNotificationId = addNotification({
          type: "progress",
          message: t('upload.uploading', { name: sessionData.name }),
          persistent: true,
        });

        const sessionResponse = await apiService.createSession(
          projectId!,
          sessionData,
        );

        // Multipart upload: upload chunks, then finalize
        let uploadId = sessionResponse.upload_id;
        try {
          const parts = await apiService.uploadPdfFile(
            pdfFile,
            sessionResponse.part_size,
            sessionResponse.part_urls,
          );
          await apiService.completeMultipartUpload(
            sessionResponse.id,
            uploadId,
            parts,
          );
        } catch (uploadError) {
          // Abort the multipart upload to clean up S3 parts
          try {
            await apiService.abortMultipartUpload(sessionResponse.id, uploadId);
          } catch (abortErr) {
            console.warn("Failed to abort multipart upload:", abortErr);
          }
          throw uploadError;
        }

        if (uploadNotificationId) {
          removeNotification(uploadNotificationId);
        }
        addNotification({
          type: "success",
          message: t('upload.success', { name: sessionData.name }),
          duration: 5000,
        });

        clearState();
        setIsUploading(false);
        loadData();
        // Refresh user to update "Pending" count
        refreshUser();
      } catch (error) {
        if (uploadNotificationId) {
          removeNotification(uploadNotificationId);
        }
        addNotification({
          type: "error",
          message: t('error.uploadFailed'),
          duration: 8000,
        });

        setIsUploading(false);
      }
    };

    uploadSession();
  };


  const handleMenuClick = () => {
    setShowMenuDropdown(!showMenuDropdown);
  };

  const handleDeleteSessions = () => {
    setIsSelectionMode(true);
    setShowMenuDropdown(false);
    setSelectedSessions(new Set());
  };

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    setSelectedSessions(new Set());
  };

  const handleSessionSelect = (sessionId: string, isSelected: boolean) => {
    const newSelected = new Set(selectedSessions);
    if (isSelected) {
      newSelected.add(sessionId);
    } else {
      newSelected.delete(sessionId);
    }
    setSelectedSessions(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedSessions.size === 0) return;

    const confirmed = window.confirm(
      t('delete.confirm', { count: selectedSessions.size })
    );

    if (!confirmed) return;

    try {
      setIsDeleting(true);
      const sessionIds = Array.from(selectedSessions);

      await apiService.deleteSessions(sessionIds);

      setSelectedSessions(new Set());
      setIsSelectionMode(false);
      loadData();
    } catch (error) {
      addNotification({
        type: "error",
        message: t('error.deleteFailed'),
        duration: 8000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const [showSortFilterModal, setShowSortFilterModal] = useState(false);
  const [currentSort, setCurrentSort] = useState("date-newest");
  const [currentFilters, setCurrentFilters] = useState<string[]>([]);

  const sortOptions: SortOption[] = [
    { id: "name-asc", label: t('sort.nameAsc'), value: "name-asc" },
    { id: "name-desc", label: t('sort.nameDesc'), value: "name-desc" },
    { id: "date-newest", label: t('sort.dateNewest'), value: "date-newest" },
    { id: "date-oldest", label: t('sort.dateOldest'), value: "date-oldest" },
  ];

  const filterOptions: FilterOption[] = [
    {
      id: "waiting_for_upload",
      label: t('filter.waitingForUpload'),
      value: "waiting_for_upload",
      checked: false,
    },
    { id: "uploading", label: t('filter.uploading'), value: "uploading", checked: false },
    { id: "queued", label: t('filter.queued'), value: "queued", checked: false },
    {
      id: "processing",
      label: t('filter.processing'),
      value: "processing",
      checked: false,
    },
    { id: "completed", label: t('filter.completed'), value: "completed", checked: false },
    { id: "failed", label: t('filter.failed'), value: "failed", checked: false },
  ];

  const handleSortFilterApply = (sortBy: string, filters: string[]) => {
    setCurrentSort(sortBy);
    setCurrentFilters(filters);
  };

  const handleSortFilterReset = () => {
    setCurrentSort("date-newest");
    setCurrentFilters([]);
  };

  const getSortedAndFilteredSessions = () => {
    let filtered = sessions.filter((session) => {
      if (currentFilters.length === 0) return true;

      return currentFilters.some((filter) => {
        return session.status === filter;
      });
    });

    filtered.sort((a, b) => {
      switch (currentSort) {
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "date-newest":
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        case "date-oldest":
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        default:
          return 0;
      }
    });

    return filtered;
  };

  const filteredSessions = getSortedAndFilteredSessions().filter((session) =>
    session.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  if (isLoading) {
    return (
      <div className="sessions-container">
        <div className="sessions-content">
          <TopBar />
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {t('loadingDocuments')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sessions-container">
      <div className="sessions-content">
        <TopBar />

        <div className="project-info">
          <div className="breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => navigate("/projects")}
            >
              {tProjects('title')}
            </span>
            <span>/</span>
            <span>{project?.name}</span>
          </div>

          <div className="project-header">
            <div>
              <h1 className="project-title">{project?.name}</h1>
              <p className="project-description">
                {project?.description || t('sessionCard.noDescription')}
              </p>
            </div>

            <div className="header-actions">
              <button
                className="add-session-btn"
                onClick={() => setShowCreateForm(true)}
                disabled={isUploading || isSelectionMode}
              >
                <span>+</span>
                {t('createSession')}
              </button>

              {isSelectionMode && selectedSessions.size > 0 && (
                <button
                  className="delete-sessions-btn"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                >
                  {isDeleting
                    ? t('delete.deleting')
                    : t('delete.deleteSelected', { count: selectedSessions.size })}
                </button>
              )}

              <div className="menu-container">
                <div className="menu-icon" onClick={handleMenuClick}>
                  ⋮
                </div>
                {showMenuDropdown && (
                  <div className="menu-dropdown">
                    <button
                      className="menu-item"
                      onClick={handleDeleteSessions}
                    >
                      {t('delete.button')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="background-processing-notice">
            {t('backgroundProcessingNotice')}
          </div>
        </div>

        <div className="main-content">
          <div className="search-section">
            <div className="search-and-filter-container">
              <div className="search-container">
                <div className="search-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21 21L16.514 16.506L21 21ZM19 10.5C19 15.194 15.194 19 10.5 19C5.806 19 2 15.194 2 10.5C2 5.806 5.806 2 10.5 2C15.194 2 19 5.806 19 10.5Z"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  className="search-input"
                  placeholder={t('search.placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button
                    className="clear-search-btn"
                    onClick={() => setSearchQuery("")}
                    aria-label={t('clearSearch')}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M18 6L6 18M6 6L18 18"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>

              <button
                className="sort-filter-btn"
                onClick={() => setShowSortFilterModal(true)}
                title={t('sortAndFilter')}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 6H21M6 12H18M9 18H15"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            {(searchQuery || currentFilters.length > 0 || currentSort !== "date-newest") && (
              <div className="search-results-info">
                {t('search.results', { filtered: filteredSessions.length, total: sessions.length })}
                {currentFilters.length > 0 && (
                  <span className="filter-info">
                    • {t('filteredBy')} {currentFilters.map(f => filterOptions.find(opt => opt.value === f)?.label || f).join(", ")}
                  </span>
                )}
                {currentSort !== "date-newest" && (
                  <span className="sort-info">
                    • {t('sortedBy')}{" "}
                    {
                      sortOptions.find((opt) => opt.value === currentSort)
                        ?.label
                    }
                  </span>
                )}
              </div>
            )}
          </div>

          {isSelectionMode && (
            <div className="selection-mode-header">
              <div className="selection-info">
                <span className="selection-count">
                  {t('selection.selected', { selected: selectedSessions.size, total: sessions.length })}
                </span>
              </div>
              <div className="selection-actions">
                <button
                  className="select-all-btn"
                  onClick={() =>
                    setSelectedSessions(
                      new Set(sessions.map((session) => session.id)),
                    )
                  }
                >
                  {t('selection.selectAll')}
                </button>
                <button
                  className="deselect-all-btn"
                  onClick={() => setSelectedSessions(new Set())}
                >
                  {t('selection.deselectAll')}
                </button>
                <button
                  className="cancel-selection-btn"
                  onClick={handleCancelSelection}
                >
                  {t('selection.cancel')}
                </button>
              </div>
            </div>
          )}

          <SessionsList
            sessions={filteredSessions}
            onSessionClick={(sessionId) => navigate(`/sessions/${sessionId}/results`)}
            isLoading={isLoading}
            isSelectionMode={isSelectionMode}
            selectedSessions={selectedSessions}
            onSessionSelect={handleSessionSelect}
            onCancelSelection={handleCancelSelection}
            retentionDays={retentionDays}
          />
        </div>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      <CreateSessionModal
        isOpen={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onNext={handleCreateSession}
        isUploading={isUploading}
        persistState={showCreateForm}
        onError={(message) => addNotification({ type: 'error', message })}
      />

      <SortFilterModal
        isOpen={showSortFilterModal}
        onClose={() => setShowSortFilterModal(false)}
        onApply={handleSortFilterApply}
        onReset={handleSortFilterReset}
        sortOptions={sortOptions}
        filterOptions={filterOptions}
        currentSort={currentSort}
        currentFilters={currentFilters}
        title={t('sortAndFilterDocuments')}
      />
    </div>
  );
};

export default Sessions;
